import DatePicker from '../../date-picker/DatePicker';
import logger from '../../../utils/logger';
import ViewOnScreen from '../../view-on-screen/ViewOnScreen';
import { FieldDetail, InputFieldComponentType } from '../../../types/order';
import { FormattedMessage, useIntl } from 'react-intl';
import { InputAdornment, MenuItem } from '@mui/material';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { z, ZodEffects, ZodObject, ZodTypeAny } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  BackendError,
  FieldLabelWrapper,
  Form,
  InvalidFieldValue,
  RowWrapper,
  TextArea,
} from './Style';
import {
  FieldLabel,
  FieldSetWrapper,
  Input,
  InputField,
  InputFieldsContainer,
  RequiredField,
  SubmitButton,
} from './Style';
import {
  getMaxDate,
  getMinDate,
} from './../../../utils/rental'

interface UpdateFormProps<T> {
  formData: T | null;
  onSubmit?: (
    data: { [key: string]: string },
    handleSetBandendError?: (errorMessageId: string) => void,
  ) => Promise<void>;
  closeAllForms?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: ZodObject<any, any, ZodTypeAny> | ZodEffects<ZodObject<any, any, ZodTypeAny>>;
  fieldDetails: FieldDetail[][];
  showSubmitButton?: boolean;
  submitButtonLabelId?: string;
  submittingButtonLabelId?: string;
}

interface DatesState {
  pickupDate: string;
  startDate: string;
  endDate: string;
}

function UpdateForm<T>({
  formData,
  schema,
  fieldDetails,
  onSubmit,
  closeAllForms,
  showSubmitButton = true,
  submitButtonLabelId = 'myOrders.updateInDesktopFormLabels.saveButtonLabel',
  submittingButtonLabelId = 'myOrders.updateInDesktopFormLabels.savingButtonLabel',
}: UpdateFormProps<T>) {
  const { formatMessage } = useIntl();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [backendError, setBackendError] = useState<boolean>(false);
  const [customError, setCustomError] = useState<string>('');
  const [dateState, setDateState] = useState<DatesState>({
    pickupDate: new Date().toUTCString(),
    startDate: new Date().toUTCString(),
    endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toUTCString(),
  });

  const handleSetBandendError = (errorMessageId: string) => {
    setCustomError(errorMessageId);
  };

  type FormFields = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
    reset,
  } = useForm<FormFields>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    shouldUnregister: true,
    defaultValues: formData ?? {},
  });

  // Trigger validation after setting form state values
  useEffect(() => {
    if (formData) {
      Object.keys(formData).forEach((key) => {
        const typedKey = key as keyof T;
        const value = formData[typedKey];

        if (typedKey == 'startDate' && typeof value === 'string' && value !== '') {
          setDateState((prev) => ({
            ...prev,
            startDate: value,
          }));
        } else if (typedKey === 'endDate' && typeof value === 'string' && value !== '') {
          setDateState((prev) => ({
            ...prev,
            endDate: value,
          }));
        } else if (typedKey == 'pickupDate' && typeof value === 'string' && value !== '') {
          setDateState((prev) => ({
            ...prev,
            pickupDate: value,
          }));
        }

        setValue(typedKey as string, value);
      });
    }
  }, [formData, setValue]);

  const onSubmitHandler: SubmitHandler<FormFields> = async (data) => {
    try {
      setIsSubmitting(true);
      setBackendError(false);
      setCustomError('');
      clearErrors('root');

      // Trigger backend API to create/update/delete the resource
      if (onSubmit) await onSubmit(data, handleSetBandendError);
      reset();

      // Closing the form for mobile view
      if (closeAllForms) closeAllForms();

      setIsSubmitting(false);
    } catch (e) {
      setBackendError(true);
      const error = e as Error;
      logger('error', 'at onSubmitHandler while trying to submit form', error);
      setIsSubmitting(false);
    }
  };

  const getDatePickerValue = (key: string) => {
    switch (key) {
      case 'pickupDate':
        return dateState.pickupDate;
      case 'startDate':
        return dateState.startDate;
      case 'endDate':
        return dateState.endDate;
      default:
        return undefined;
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmitHandler)}>
      <InputFieldsContainer>
        {fieldDetails.map((fieldGroup, groupIndex) => (
          <RowWrapper key={`row-${groupIndex}`}>
            {fieldGroup.map((field) => {
              if (field.visible) {
                return (
                  <FieldSetWrapper key={`${groupIndex}-${field.fieldKey}`}>
                    <FieldLabel containsSubLabel={!!field.fieldSecondaryLabelId}>
                      <FieldLabelWrapper>
                        <FormattedMessage id={field.fieldLabelId} />
                        {field.required && <RequiredField component={'span'}> *</RequiredField>}
                      </FieldLabelWrapper>
                      {field.fieldSecondaryLabelId && (
                        <FieldLabelWrapper
                          isSubLabel
                          onClick={() => field.onSubLabelClick && field.onSubLabelClick()}
                        >
                          <FormattedMessage id={field.fieldSecondaryLabelId} />
                        </FieldLabelWrapper>
                      )}
                    </FieldLabel>
                    {field.inputComponentType === InputFieldComponentType.TEXTFIELD && (
                      <InputField
                        {...register(field.fieldKey as string)}
                        disabled={field.disabled}
                        name={field.fieldKey}
                        type={field.inputType}
                        inputMode={field.inputMode}
                        onChange={() => clearErrors(field.fieldKey as string)}
                        InputProps={{
                          startAdornment: field.adornment && (
                            <InputAdornment position='start'>{field.adornment}</InputAdornment>
                          ),
                        }}
                        adornmentBackgroundColor={!!field.adornment}
                      ></InputField>
                    )}
                    {field.inputComponentType === InputFieldComponentType.DATE && (
                      <DatePicker
                        minDate={(field.fieldKey === "pickupDate" ? getMinDate() : null)}
                        maxDate={(field.fieldKey === "pickupDate" ? getMaxDate() : null)}
                        value={getDatePickerValue(field.fieldKey)}
                        dateChangeHandler={(date) => {
                          setValue(field.fieldKey as string, date.toISOString());
                          clearErrors(field.fieldKey as string);
                        }}
                        showIcon
                        isStyledForFormDatePicker
                      />
                    )}
                    {field.inputComponentType === InputFieldComponentType.TEXTAREA && (
                      <TextArea
                        {...register(field.fieldKey as string)}
                        placeholder={formatMessage({ id: field.placeholderId })}
                      />
                    )}
                    {field.inputComponentType === InputFieldComponentType.SELECT && (
                      <Input
                        select
                        {...register(field.fieldKey as string)}
                        disabled={field.disabled}
                        name={field.fieldKey}
                        type={field.inputType}
                        inputMode={field.inputMode}
                        defaultValue={field.defaultSelectOption}
                        onChange={() => clearErrors(field.fieldKey as string)}
                      >
                        {field.selectOptions?.map((option) => (
                          <MenuItem key={option.label} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Input>
                    )}
                    {errors[field.fieldKey as string] && (
                      <InvalidFieldValue>
                        {errors[field.fieldKey as string]?.message as string}
                      </InvalidFieldValue>
                    )}
                  </FieldSetWrapper>
                );
              }
              return null;
            })}
          </RowWrapper>
        ))}
        {errors.root?.message && (
          <InvalidFieldValue>{errors.root?.message as string}</InvalidFieldValue>
        )}
      </InputFieldsContainer>
      {(customError || backendError) && (
        <BackendError>
          <FormattedMessage id={customError ?? 'myProfile.backendError'} />
        </BackendError>
      )}
      {showSubmitButton && (
        <SubmitButton disabled={isSubmitting} type='submit'>
          <ViewOnScreen screen={{ down: 'sm' }}>
            {isSubmitting ? (
              <FormattedMessage id={submittingButtonLabelId} />
            ) : (
              <FormattedMessage id={submitButtonLabelId} />
            )}
          </ViewOnScreen>
          <ViewOnScreen screen={{ up: 'sm' }}>
            {isSubmitting ? (
              <FormattedMessage id={submittingButtonLabelId} />
            ) : (
              <FormattedMessage id={submitButtonLabelId} />
            )}
          </ViewOnScreen>
        </SubmitButton>
      )}
    </Form>
  );
}

export default UpdateForm;
